import React, { useCallback, useState } from "react";
import { useLayoutEffect } from "react";
import { AiFillCaretDown, AiFillCaretUp } from "react-icons/ai";
import OutsideClickHandler from "react-outside-click-handler";
import styled from "styled-components";

import { SelectionValue } from "../types/select";

interface Props<T> {
    options: Array<SelectionValue<T>>;
    onChangeHandler: (value: SelectionValue<T>["value"]) => void;
    initialValue?: SelectionValue<T>;
    initialMode?: boolean;
    disabled?: boolean;
}

const CustomSelect = <T extends {}>({
    options,
    onChangeHandler,
    initialValue = options[0],
    initialMode = false,
    disabled = false,
}: Props<T>) => {
    const [isSelectShown, setShown] = useState(false);
    const [value, setValue] = useState(initialValue);

    useLayoutEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    const handleSelect = useCallback(
        (selectedValue: SelectionValue<T>) => {
            if (initialMode) {
                setValue(
                    options.filter((option) => option !== selectedValue)[0]
                );
            } else {
                setValue(selectedValue);
            }
            setShown(false);
            onChangeHandler(selectedValue.value);
        },
        [onChangeHandler, options, initialMode]
    );

    const dropdownDisplay = React.useMemo(
        () =>
            options.map((option) => (
                <Selection
                    key={option.display}
                    onClick={() => !disabled && handleSelect(option)}
                >
                    {option.display}
                </Selection>
            )),
        [options, handleSelect, disabled]
    );

    return (
        <OutsideClickHandler
            onOutsideClick={() => isSelectShown && setShown(!isSelectShown)}
        >
            <Container isDisabled={disabled}>
                <SelectBar
                    onClick={() =>
                        !disabled && options.length && setShown(!isSelectShown)
                    }
                >
                    <div>
                        {initialMode
                            ? options.length
                                ? "Press to select..."
                                : "No options to select"
                            : options.length
                            ? value?.display || options[0].display
                            : "No options to select"}
                    </div>
                    <div>
                        {!disabled ? (
                            isSelectShown ? (
                                <AiFillCaretUp />
                            ) : (
                                <AiFillCaretDown />
                            )
                        ) : null}
                    </div>
                </SelectBar>
                {isSelectShown && <Dropdown>{dropdownDisplay}</Dropdown>}
            </Container>
        </OutsideClickHandler>
    );
};

const Container = styled.div<{ isDisabled?: boolean }>`
    position: relative;
    width: inherit;
    height: inherit;

    &:hover {
        cursor: ${(props) => (props.isDisabled ? "default" : "pointer")};
    }
`;

const SelectBar = styled.div`
    display: flex;
    justify-content: space-between;
    border: solid 1px ${(props) => props.theme.colors.neutral[300]};
    background: solid 1px ${(props) => props.theme.colors.neutral[200]};
    color: ${(props) => props.theme.colors.neutral[600]};
    border-radius: 0.2rem;

    height: inherit;
    padding: 0 0.5rem;

    & > * {
        display: flex;
        justify-content: center;
        align-items: center;
    }
`;

const Dropdown = styled.div`
    position: relative;
    z-index: 2;
    min-width: 100%;
    width: fit-content;
    border: solid 0.075rem ${(props) => props.theme.colors.neutral[300]};

    display: flex;
    flex-flow: column;
`;

const Selection = styled.div`
    padding-left: 0.5rem;
    display: flex;
    align-items: center;
    background: white;
    color: ${(props) => props.theme.colors.neutral[600]};
    overflow: hidden;
    text-overflow: ellipsis;
    height: 3rem;
    white-space: nowrap;
    border-bottom: solid 0.5px ${(props) => props.theme.colors.neutral[200]};

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: ${(props) => props.theme.colors.neutral[100]};
        color: ${(props) => props.theme.colors.neutral[700]};
    }
`;

export default CustomSelect;
